import { CardContainer, CardDisplay, CardDisplayProps } from '@/components/displays/CardDisplay';
import { capture } from '@/handlers/error';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { ResizeBorder } from '@/pages/LessonEditor/components/ResizeBorder';
import { useComponentEditor } from '@/providers/editor/EditorProvider';
import { Coordinate, isSlideComponentCharacterCards, PropsWithPreview, reposition, Size, SlideComponent } from '@/types/slide';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import SettingsIcon from '@mui/icons-material/Settings';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Box, Stack } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import { CSSProperties, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { WordPicker } from '../../inputs/WordPicker/WordPicker';
import { ComponentLabel } from '../ComponentLabel';
import { ContextualBar } from '../ContextualBar';
import { useRightClickMenu } from '../hooks/useRightClickMenu';
import { useSlideComponent } from '../hooks/useSlideComponent';
import { RightClickMenu } from '../RightClickMenu';
import { SlideRef } from '../Slide';
import { QuestionSettingsPopover } from './QuestionSettingsPopover';

const CharacterCardAnswerOverlay = (props: Pick<CardDisplayProps, 'direction'> & { opacity?: CSSProperties['opacity'] }) => {
    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                opacity: props.opacity ?? 1,
            }}
        >
            <Box
                sx={(theme) => ({
                    borderStyle: 'solid',
                    borderRadius: 4,
                    borderColor: theme.palette.grey[500],
                    background: `url(${props.direction === 'row' ? `/platform/images/kcard-vertical.svg` : `/platform/images/kcard-horizontal.svg`})`,
                    backgroundColor: theme.palette.background.paper,
                    borderWidth: 4,
                    width: props.direction === 'row' ? 312 : 468,
                    height: props.direction === 'row' ? 468 : 312,
                    alignSelf: 'center',
                    justifySelf: 'center',
                })}
            />
        </Box>
    );
};

const CharacterCard = (props: PropsWithChildren<CardDisplayProps>) => {
    return (
        <CardDisplay
            sx={(theme) => ({
                boxShadow: 0,
                borderStyle: 'solid',
                borderRadius: 4,
                borderColor: theme.palette.grey[500],
                borderWidth: 4,
                backgroundColor: theme.palette.background.paper,
                width: props.direction === 'row' ? 312 : 468,
                height: props.direction === 'row' ? 468 : 312,
            })}
            {...props}
        >
            {props.children}
        </CardDisplay>
    );
};

const NoAnswerCardDisplay = (props: CardDisplayProps) => {
    return (
        <CharacterCard aria-label='no-answer-card' {...props}>
            <QuestionMarkIcon sx={(theme) => ({ width: 240, height: 240, color: theme.palette.grey[500] })} />
        </CharacterCard>
    );
};

interface Props {
    ref: React.RefObject<HTMLDivElement | null>;
    init: {
        model: SlideComponent;
        focus?: boolean;
    };
    slideRef: React.RefObject<SlideRef | null>;
}

export const SlideComponentCharacterCards: React.FC<PropsWithPreview<Props>> = ({ ref, init, preview, slideRef }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { deleteComponent, updateComponent, bringToFront, bringToBack, copyComponent, pasteComponent, undo, redo } = useComponentEditor();

    const { rightClickMenu, handleRightClickMenu, rightClickMenuClose } = useRightClickMenu();

    // Add refs for the popovers
    const wordPickerRef = useRef<HTMLDivElement | null>(null);

    const { isSelected } = useSlideComponent(ref, [], !!preview);

    const [model, setModel] = useState<SlideComponent>({ ...init.model });
    const [isFocus, setIsFocus] = useState<boolean>(init.focus ?? false);

    const [tSize, setTSize] = useState<Size>(model.size);
    const [tPos, setTPos] = useState<Coordinate>(model.pos);

    const [revealAnswer, setRevealAnswer] = useState<boolean>(false);

    // Add state for word picker popover
    const [wordPickerAnchorEl, setWordPickerAnchorEl] = useState<HTMLElement | null>(null);

    // Add state for character cards question settings  popover
    const [questionSettingsAnchorEl, setQuestionSettingsAnchorEl] = useState<HTMLElement | null>(null);

    const isEditing = isFocus || isSelected;

    const onDeleteComponent = useCallback(
        async (id: string) => {
            try {
                await deleteComponent(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [deleteComponent, notifications],
    );

    const onUpdateComponent = useCallback(
        async (id: string, component: SlideComponent) => {
            try {
                await updateComponent(id, component);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [updateComponent, notifications],
    );

    const onBringToFrontHandler = useCallback(
        async (id: string) => {
            try {
                await bringToFront(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToFront, notifications],
    );

    const onBringToBackHandler = useCallback(
        async (id: string) => {
            try {
                await bringToBack(id);
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error' });
                capture(error);
            }
        },
        [bringToBack, notifications],
    );

    const debouncedUpdateComponent = useDebounce(onUpdateComponent, 500);

    useOutsideClick(ref, () => {
        if (preview) return;
        if (!ref) return;

        setIsFocus(false);
    });

    useEffect(() => {
        if (isFocus && !preview) {
            setWordPickerAnchorEl(ref.current);
        }
    }, [isFocus, preview, ref]);

    if (!isSlideComponentCharacterCards(model)) {
        return null;
    }

    const getComponentSize = (cardType?: number) => {
        let size: Size;

        if (cardType == 2) {
            //size for 2 horizontal cards
            size = {
                width: 468,
                height: 644,
            };
        } else if (cardType == 3) {
            // size for in-out
            size = {
                width: 468,
                height: 468,
            };
        } else {
            //size for 2 vertical cards (default)
            size = {
                width: 644,
                height: 468,
            };
        }

        return size;
    };

    const locale = model.content?.locale ?? 'zh-HK';
    const img1 =
        model.content.serial1?.length > 0
            ? model.content.cardType == 3
                ? `/asset/card/Transparent/${locale}/${model.content.serial1[0]}.png`
                : `/asset/card/${locale}/${model.content.serial1[0]}.png`
            : undefined;
    const img2 = model.content.serial2?.length > 0 ? `/asset/card/${locale}/${model.content.serial2[0]}.png` : undefined;

    return (
        <Box
            onContextMenu={isEditing ? handleRightClickMenu : undefined}
            ref={ref}
            id={`characterCards-component-${model.id}`}
            data-testid={`slide-component-character-cards`}
            sx={{
                position: 'absolute',
                left: tPos.x,
                top: tPos.y,
                m: 'inherit',
                p: 'inherit',
                textAlign: 'left',
                width: tSize.width,
                height: tSize.height,
            }}
            onClick={(e) => {
                if (!preview) {
                    e.stopPropagation();
                }
            }}
            onDoubleClick={(e) => {
                if (preview) return;
                e.stopPropagation();
                setWordPickerAnchorEl(ref.current);
            }}
        >
            <ResizeBorder
                resizable={false}
                size={tSize}
                pos={tPos}
                preview={preview}
                isSelected={isEditing}
                onResize={(size, pos) => {
                    setTSize(size);
                    setTPos(pos);
                    const updateModel = { ...model, size, pos };
                    setModel({ ...updateModel });
                    debouncedUpdateComponent?.(model.id, { ...updateModel });
                }}
                slideRef={slideRef}
            >
                {!preview && (
                    <ComponentLabel
                        variant='caption'
                        sx={{
                            ...(!isEditing && {
                                opacity: 0, // Hide by default
                                transition: 'opacity 0.2s ease',
                                [`#characterCards-component-${model.id}:hover &`]: {
                                    opacity: 1, // Show when text-component is hovered
                                },
                            }),
                        }}
                        noWrap
                    >
                        {t('slide.components.characterCards.title')}
                    </ComponentLabel>
                )}

                <Box
                    sx={{
                        width: tSize.width,
                        height: tSize.height,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {model.content.cardType == 1 ? (
                        <Box>
                            <Stack direction={'row'} sx={{ height: '100%', width: '100%', gap: '20px' }}>
                                <CardContainer>
                                    <CharacterCard imgUrl={img1} direction={'row'} orientation={'Up'} />
                                    <CharacterCardAnswerOverlay direction={'row'} opacity={model.content.settings.gameType == 'Second' ? 0 : revealAnswer ? 0.5 : 1} />
                                </CardContainer>
                                <CardContainer>
                                    <CharacterCard imgUrl={img2} direction={'row'} orientation={'Up'} />
                                    <CharacterCardAnswerOverlay direction={'row'} opacity={model.content.settings.gameType == 'First' ? 0 : revealAnswer ? 0.5 : 1} />
                                </CardContainer>
                            </Stack>
                        </Box>
                    ) : model.content.cardType == 2 ? (
                        <Box>
                            <Stack direction={'column'} sx={{ height: '100%', width: '100%', gap: '20px' }}>
                                <CardContainer>
                                    <CharacterCard imgUrl={img1} direction={'column'} orientation={'Right'} />
                                    <CharacterCardAnswerOverlay direction={'column'} opacity={model.content.settings.gameType == 'Second' ? 0 : revealAnswer ? 0.5 : 1} />
                                </CardContainer>
                                <CardContainer>
                                    <CharacterCard imgUrl={img2} direction={'column'} orientation={'Right'} />
                                    <CharacterCardAnswerOverlay direction={'column'} opacity={model.content.settings.gameType == 'First' ? 0 : revealAnswer ? 0.5 : 1} />
                                </CardContainer>
                            </Stack>
                        </Box>
                    ) : model.content.cardType == 3 ? (
                        <>
                            <img
                                src={img1}
                                style={{
                                    position: 'absolute',
                                    top: (tSize.height - 644) / 2,
                                    left: (tSize.width - 644) / 2,
                                    width: 644,
                                    height: 644,
                                    opacity: model.content.settings.gameType == 'Both' ? (revealAnswer ? 0.5 : 0) : 1,
                                }}
                            />
                            <CardContainer>
                                <CharacterCard imgUrl={img2} direction={'row'} orientation={'Up'} />
                                <CharacterCardAnswerOverlay direction={'row'} opacity={revealAnswer ? 0.5 : 1} />

                                {model.content.settings.gameType == 'Both' && !revealAnswer && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: (tSize.width - 312) / 2 - 24,
                                            width: 312,
                                            height: 468,
                                        }}
                                    >
                                        <CharacterCardAnswerOverlay direction={'row'} />
                                    </Box>
                                )}
                            </CardContainer>
                        </>
                    ) : (
                        // default
                        <Box>
                            <Stack direction={'row'} sx={{ height: '100%', width: '100%', gap: '20px' }}>
                                <CardContainer>
                                    <NoAnswerCardDisplay direction={'row'} />
                                </CardContainer>
                                <CardContainer>
                                    <NoAnswerCardDisplay direction={'row'} />
                                </CardContainer>
                            </Stack>
                        </Box>
                    )}
                </Box>

                {!preview && isEditing && (
                    <ContextualBar
                        startButtons={[
                            {
                                icon: <EditIcon />,
                                tooltip: t('slide.components.characterCards.tooltip.edit'),
                                onClick: () => {
                                    setWordPickerAnchorEl(ref.current);
                                },
                            },
                        ]}
                        midButtons={
                            model.content.character
                                ? [
                                      {
                                          icon: <SettingsIcon />,
                                          tooltip: t('slide.components.characterCards.tooltip.questionSettings'),
                                          onClick: () => {
                                              setQuestionSettingsAnchorEl(ref.current);
                                          },
                                      },
                                      {
                                          icon: revealAnswer ? <VisibilityOff /> : <Visibility />,
                                          tooltip: revealAnswer ? t('slide.components.characterCards.tooltip.hideAnswer') : t('slide.components.characterCards.tooltip.revealAnswer'),
                                          onClick: () => {
                                              setRevealAnswer(!revealAnswer);
                                          },
                                      },
                                  ]
                                : undefined
                        }
                        endButtons={[
                            {
                                icon: <DeleteIcon />,
                                tooltip: t('slide.components.characterCards.tooltip.remove'),
                                onClick: () => {
                                    onDeleteComponent?.(model.id);
                                },
                            },
                        ]}
                    />
                )}

                {/* error message when model.character is empty */}
                {!model.content?.character && <Box sx={{ color: 'error.main', mt: 1 }}>{t('slide.components.characterCards.warningMessage')}</Box>}

                {wordPickerAnchorEl && (
                    <WordPicker
                        type='card'
                        title={t('slide.legacy.character.characterSearch')}
                        anchorEl={wordPickerAnchorEl}
                        slotProps={{
                            paper: {
                                ref: wordPickerRef, // Attach the ref to the Paper element
                            },
                        }}
                        value={{
                            phrase: model.content?.character,
                        }}
                        onClose={() => {
                            setWordPickerAnchorEl(null);
                        }}
                        onUpdate={(settings) => {
                            const size = getComponentSize(settings?.characterList?.[0]?.cardType);
                            setTSize(size);

                            const updatedContent = {
                                ...model.content,
                                code: settings?.characterList?.[0]?.code,
                                locale: settings?.characterList?.[0]?.locale,
                                character: settings?.characterList?.[0]?.character,
                                cardType: settings?.characterList?.[0]?.cardType,
                                serial1: settings?.characterList?.[0]?.serial1,
                                serial2: settings?.characterList?.[0]?.serial2,
                                settings: {
                                    gameType: 'Second',
                                },
                            };

                            let updateModel = { ...model, content: updatedContent, size: size };
                            updateModel = reposition(updateModel, false);

                            setModel(updateModel);
                            onUpdateComponent?.(model.id, updateModel);
                        }}
                    />
                )}

                {questionSettingsAnchorEl && (
                    <QuestionSettingsPopover
                        anchorEl={questionSettingsAnchorEl}
                        value={{
                            cardType: model.content.cardType,
                            gameType: model.content.settings.gameType,
                        }}
                        onClose={() => {
                            setQuestionSettingsAnchorEl(null);
                        }}
                        onUpdate={(settings) => {
                            const updatedContent = {
                                ...model.content,
                                settings: {
                                    gameType: settings.gameType,
                                },
                            };

                            const updateModel = { ...model, content: updatedContent };

                            setModel(updateModel);
                            onUpdateComponent?.(model.id, updateModel);
                        }}
                    />
                )}
            </ResizeBorder>

            <RightClickMenu
                componentId={model.id}
                contextMenu={rightClickMenu}
                handleClose={rightClickMenuClose}
                bringToFront={onBringToFrontHandler}
                bringToBack={onBringToBackHandler}
                copyComponent={copyComponent}
                pasteComponent={pasteComponent}
                undo={undo}
                redo={redo}
            />
        </Box>
    );
};
